import React, { useState, useEffect } from 'react';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { Phone, Mail, MessageCircle, ArrowLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// --- Sub-Component for Individual Member Card ---
const MemberCard = ({ member }) => {
    // State to track if image fails to load
    const [imgError, setImgError] = useState(false);

    // Helper to get initials
    const getInitials = (name) => {
        if (!name) return 'MB';
        const parts = name.trim().split(' ');
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    return (
        <div className="group bg-white rounded-[20px] p-6 shadow-[0_2px_15px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.1)] border border-gray-100 transition-all duration-300 flex flex-col items-center h-full hover:-translate-y-1">
            
            {/* Profile Picture / Initials Logic - CENTERED */}
            <div className="w-20 h-20 rounded-full mb-4 overflow-hidden flex items-center justify-center shadow-sm bg-orange-50 mx-auto">
                {!imgError && member.profile_picture ? (
                    <img 
                        src={member.profile_picture} 
                        alt="" 
                        onError={() => setImgError(true)} 
                        className="w-full h-full object-cover"
                    />
                ) : (
                    // Fallback: Initials in ORANGE
                    <span className="text-[#FB9724] text-2xl font-bold">
                        {getInitials(member.full_name)}
                    </span>
                )}
            </div>

            {/* Name */}
            <h3 className="text-lg font-bold text-gray-900 text-center mb-1 line-clamp-1">{member.full_name}</h3>
            
            {/* Business Name / Industry - BLUE Color */}
            <span className="text-[#1A87CF] font-medium text-sm mb-1 text-center line-clamp-1">
                {member.industry}
            </span>
            
            {/* Chapter Name */}
            <p className="text-gray-400 text-xs text-center mb-6">
                {member.chapter_name || 'General'} Chapter
            </p>
            
            {/* Action Buttons (Call, Chat, Email) */}
            <div className="mt-auto grid grid-cols-3 gap-3 w-full">
                {/* Call Button */}
                <a 
                    href={`tel:${member.whatsapp_number}`} 
                    className="flex items-center justify-center py-2.5 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300"
                    title="Call"
                >
                    <Phone size={16} strokeWidth={2.5} />
                    <span className="ml-1.5 text-xs font-semibold hidden sm:inline">Call</span>
                </a>

                {/* Chat Button (WhatsApp) */}
                <a 
                    href={`https://wa.me/${member.whatsapp_number}`} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="flex items-center justify-center py-2.5 rounded-xl bg-green-50 text-green-600 hover:bg-green-600 hover:text-white transition-all duration-300"
                    title="Chat"
                >
                    <MessageCircle size={16} strokeWidth={2.5} />
                    <span className="ml-1.5 text-xs font-semibold hidden sm:inline">Chat</span>
                </a>

                {/* Email Button */}
                <a 
                    href={`mailto:${member.email}`} 
                    className="flex items-center justify-center py-2.5 rounded-xl bg-orange-50 text-orange-600 hover:bg-orange-500 hover:text-white transition-all duration-300"
                    title="Email"
                >
                    <Mail size={16} strokeWidth={2.5} />
                    <span className="ml-1.5 text-xs font-semibold hidden sm:inline">Email</span>
                </a>
            </div>
        </div>
    );
};

// --- Main Page Component ---
const AllMembers = () => {
    const navigate = useNavigate();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchAllMembers = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/get-our-member?role=member`);
                const result = await response.json();
                if (result.success) {
                    setMembers(result.data);
                }
            } catch (error) {
                console.error("Error fetching members:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAllMembers();
    }, []);

    // Filter Logic
    const filteredMembers = members.filter(member => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return true; 

        const name = member.full_name ? member.full_name.toLowerCase() : '';
        const business = member.industry ? member.industry.toLowerCase() : ''; 
        const chapter = member.chapter_name ? member.chapter_name.toLowerCase() : '';

        return name.includes(term) || business.includes(term) || chapter.includes(term);
    });

    return (
        <div className="font-sans text-gray-900 bg-gray-50 min-h-screen">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Rubik:wght@400;500;600;700;800&display=swap');
                .font-rubik { font-family: 'Rubik', sans-serif; }
                .section-header:after { content: ''; position: absolute; bottom: -10px; left: 0; width: 50%; height: 4px; background: #FB9724; border-radius: 2px; }
                .section-header { position: relative; display: inline-block; }
            `}</style>

            <Navbar scrollToSection={() => {}} /> 

            <div className="pt-28 pb-16 container mx-auto px-4 sm:px-6">
                
                {/* Header Section */}
                <div className="max-w-7xl mx-auto mb-12">
                    <button 
                        onClick={() => navigate('/')} 
                        className="flex items-center text-[#1A87CF] font-medium mb-8 hover:underline transition-all"
                    >
                        <ArrowLeft size={20} className="mr-2" /> Back to Home
                    </button>

                    <div className="flex flex-col md:flex-row justify-between items-end md:items-center gap-6">
                        <div>
                            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-rubik section-header">
                                All <span className="text-[#1A87CF]">Members</span>
                            </h1>
                            <p className="text-xl text-gray-600 mt-2">
                                Discover and connect with {members.length}+ professionals.
                            </p>
                        </div>
                        
                        {/* Search Bar */}
                        <div className="relative w-full md:w-80">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input 
                                type="text" 
                                placeholder="Search Name or Business..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FB9724] shadow-sm bg-white"
                            />
                        </div>
                    </div>
                </div>

                {/* Grid Section */}
                <div className="max-w-7xl mx-auto">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FB9724]"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredMembers.length > 0 ? (
                                // FIX: Added index to key to prevent duplicates error
                                filteredMembers.map((member, index) => (
                                    <MemberCard 
                                        key={`${member._id}-${index}`} 
                                        member={member} 
                                    />
                                ))
                            ) : (
                                <div className="col-span-full text-center py-20">
                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                                        <Search className="text-gray-400" size={24} />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900">No members found</h3>
                                    <p className="text-gray-500">Try adjusting your search terms.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <Footer scrollToSection={() => {}} />
        </div>
    );
};

export default AllMembers;